/* eslint-disable camelcase */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReportGmailerrorred from '@mui/icons-material/ReportGmailerrorred';

import { Icon } from 'uikit/icons';

import useIsMyLearningEnabled from '../hooks/useIsMyLearningEnabled';

import {
  getMainTextContent,
  getMetaContent,
  getActions,
} from '../helpers/enrollmentMessageHelpers';

import styles from '../styles/enrollmentMessage.module.scss';

const EnrollmentMessage = ({ courseType = 'Course', enrollmentMetadata = {} }) => {
  const isFeatureEnabled = useIsMyLearningEnabled();

  if (!enrollmentMetadata || !isFeatureEnabled) return null;

  const {
    is_dropped: isDropped,
    is_overdue: isOverdue,
    relationship_type: relationshipType,
    status,
  } = enrollmentMetadata;

  const isRequired = relationshipType === 'required';
  const isRecommended = relationshipType === 'recommended';

  let style = 'default';

  const shouldShowInfo =
    ['required', 'recommended', 'in-progress', 'assessment-completed', 'incomplete'].includes(
      status,
    ) &&
    !isOverdue &&
    !isDropped;
  const shouldShowSuccess = ['passed'].includes(status);
  const shouldShowWarning = (['failed', 'no-show'].includes(status) || isOverdue) && !isDropped;

  if (shouldShowInfo) {
    style = 'info';
  } else if (shouldShowSuccess) {
    style = 'success';
  } else if (shouldShowWarning) {
    style = 'warning';
  }

  if (!isRequired && !isRecommended) return null;

  const mainTextContent = getMainTextContent(enrollmentMetadata);
  const metaContent = getMetaContent(enrollmentMetadata);
  const actions = getActions(enrollmentMetadata, courseType);

  let icon = <ReportGmailerrorred sx={{ width: 18, height: 18 }} />;

  if (style === 'success') {
    icon = <TaskAltIcon sx={{ width: 18, height: 18 }} />;
  } else if (isRecommended) {
    icon = <Icon icon="starOutlineCircle" size={18} />;
  }

  return (
    <div className={classnames(styles.element, { [styles[style]]: style })}>
      <div className={styles.content}>
        <div className={styles.mainText}>
          {icon}
          {mainTextContent}
        </div>
        <div className={styles.meta}>{metaContent}</div>
      </div>
      <div className={styles.actions}>{actions}</div>
    </div>
  );
};

EnrollmentMessage.propTypes = {
  courseType: PropTypes.string,
  enrollmentMetadata: PropTypes.shape({
    relationship_type: PropTypes.string,
    status: PropTypes.string,
    relationship_reason: PropTypes.string,
    enrolled_on: PropTypes.string,
    created_by: PropTypes.string,
    completed_on: PropTypes.string,
    expires_on: PropTypes.string,
    due_on: PropTypes.string,
  }),
};

export default EnrollmentMessage;
